import { inject, Injectable } from '@angular/core';
import { addDoc, collection, collectionData, deleteDoc, doc, Firestore, getDoc, query, setDoc, Timestamp, updateDoc, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { AuthService } from '../../core/auth/auth.service';
import { Appointment, AppointmentStatus, UpdateAppointmentStatusRequest, UpdateAppointmentStatusResponse } from '../../core/models';
import { AppointmentEventService } from '../../core/services/appointment-event.service';
import { PathConstants } from '../../db.paths';

@Injectable({ providedIn: 'root' })
export class AppointmentService {
  private firestore = inject(Firestore);
  private authService = inject(AuthService);
  private appointmentEventService = inject(AppointmentEventService);
  private functions = inject(Functions);
  private updateApptStatusFn = httpsCallable<UpdateAppointmentStatusRequest, UpdateAppointmentStatusResponse>(
    this.functions,
    'updateAppointmentStatus'
  );

  constructor() {
    // Initialization handled by dependency injection
  }

  /**
   * Fetches all appointments for the logged-in user with real-time updates.
   * Automatically handles caching and live updates via Firestore's onSnapshot.
   *
   * @returns Observable stream of all active (non-cancelled) appointments
   */
  getAppointments(): Observable<Appointment[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user?.uid) {
          console.warn('No user is logged in. Returning an empty array.');
          return of([]);
        }

        const appointmentsPath = PathConstants.getAppointmentsPath(user.uid);
        const appointmentsCollection = collection(this.firestore, appointmentsPath);

        return collectionData(appointmentsCollection, { idField: 'id' }).pipe(
          map((appointments: any[]) => this.transformAndFilterAppointments(appointments)),
          catchError((error) => {
            console.error('Error fetching appointments:', error);
            return of([]);
          })
        );
      })
    );
  }

  /**
   * Fetches appointments for a specific patient.
   * Filters out cancelled and rescheduled appointments, and transforms timestamp fields.
   *
   * @param patientKey The unique key of the patient
   * @returns Observable stream of patient's appointments
   */
  getAppointmentsByPatient(patientKey: string): Observable<Appointment[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.warn('No user is logged in. Returning an empty array.');
          return of([]);
        }
        const appointmentsPath = PathConstants.getAppointmentsPath(user.uid);
        const q = query(collection(this.firestore, appointmentsPath), where('patientKey', '==', patientKey));
        return collectionData(q, { idField: 'id' }).pipe(
          map((appointments: any[]) => this.transformAndFilterAppointments(appointments)),
          catchError((error: any) => {
            console.error(`Error fetching appointments for patient ${patientKey}:`, error);
            return of([]);
          })
        );
      })
    );
  }

  getAppointmentsForDates(startDate: Date, endDate: Date): Observable<Appointment[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.warn('No user is logged in. Returning an empty array.');
          return of([]);
        }
        const appointmentsPath = PathConstants.getAppointmentsPath(user.uid);
        const q = query(
          collection(this.firestore, appointmentsPath),
          where('start', '>=', Timestamp.fromDate(startDate)),
          where('start', '<=', Timestamp.fromDate(endDate))
        );
        return collectionData(q, { idField: 'id' }).pipe(
          map((appointments: any[]) => this.transformAndFilterAppointments(appointments)),
          catchError((error: any) => {
            console.error(
              `Error fetching appointments for dates ${startDate.toISOString()} - ${endDate.toISOString()}:`,
              error
            );
            return of([]);
          })
        );
      })
    );
  }

  getAppointmentById(id: string): Observable<Appointment | undefined> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.warn('No user is logged in. Returning undefined.');
          return of(undefined);
        }
        const docRef = doc(this.firestore, PathConstants.getAppointmentsPath(user.uid), id);
        return from(getDoc(docRef)).pipe(
          map((docSnapshot) => {
            if (docSnapshot.exists()) {
              const data = docSnapshot.data() as Appointment;
              return {
                ...data,
                id: docSnapshot.id,
                start: data['start'] ? data['start'] : null,
                end: data['end'] ? data['end'] : null,
                link: data['nots'] ? this.extractHttpLink(data['notes']) : null,
              } as Appointment;
            } else {
              return undefined;
            }
          }),
          catchError((error: any) => {
            console.error(`Error fetching appointment by ID ${id}:`, error);
            return of(undefined);
          })
        );
      })
    );
  }

  getBlockedAppointments(): Observable<any[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.warn('No user is logged in. Returning an empty array.');
          return of([]);
        }
        const calendarPath = PathConstants.getCalendarPath(user.uid);
        const calendarCollection = collection(this.firestore, calendarPath);
        return collectionData(calendarCollection, { idField: 'id' }).pipe(
          map((blockedSlots) =>
            blockedSlots.map((slot) => ({
              ...slot,
              start: slot['start'] ? (slot['start'] as Timestamp).toDate() : null,
              end: slot['end'] ? (slot['end'] as Timestamp).toDate() : null,
            }))
          ),
          catchError((error: any) => {
            console.error('Error fetching blocked appointments:', error);
            return of([]);
          })
        );
      })
    );
  }

  /**
   * Fetches blocked time slots for a specific date range.
   * Filters appointments with start dates within the provided range.
   *
   * @param startDate Start of the date range (inclusive)
   * @param endDate End of the date range (inclusive)
   * @returns Observable stream of blocked slots within the date range
   */
  getBlockedAppointmentsForDates(startDate: Date, endDate: Date): Observable<any[]> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.warn('No user is logged in. Returning an empty array.');
          return of([]);
        }
        const calendarPath = PathConstants.getCalendarPath(user.uid);
        const q = query(
          collection(this.firestore, calendarPath),
          where('start', '>=', Timestamp.fromDate(startDate)),
          where('start', '<=', Timestamp.fromDate(endDate))
        );
        return collectionData(q, { idField: 'id' }).pipe(
          map((blockedSlots) =>
            blockedSlots.map((slot) => ({
              ...slot,
              start: slot['start'] ? (slot['start'] as Timestamp).toDate() : null,
              end: slot['end'] ? (slot['end'] as Timestamp).toDate() : null,
            }))
          ),
          catchError((error: any) => {
            console.error(
              `Error fetching blocked appointments for dates ${startDate.toISOString()} - ${endDate.toISOString()}:`,
              error
            );
            return of([]);
          })
        );
      })
    );
  }

  saveBlockedAppointment(slots: any | any[]): Observable<void> {
    const slotsToSave = Array.isArray(slots) ? slots : [slots];

    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.error('User not logged in. Cannot save blocked appointment.');
          return of(void 0); // Return an observable that completes without emitting a value
        }

        const calendarPath = PathConstants.getCalendarPath(user.uid);
        const calendarCollection = collection(this.firestore, calendarPath);

        const savePromises = slotsToSave.map((slot) => {
          const newSlot = { ...slot };
          if (newSlot.start instanceof Date) {
            newSlot.start = Timestamp.fromDate(newSlot.start);
          } else if (!newSlot.start) {
            newSlot.start = Timestamp.fromDate(new Date());
          }
          if (newSlot.end instanceof Date) {
            newSlot.end = Timestamp.fromDate(newSlot.end);
          }

          const docId = `block_${newSlot.start.toMillis()}_${newSlot.end.toMillis()}`;
          return setDoc(doc(calendarCollection, docId), newSlot);
          // return addDoc(calendarCollection, newSlot)
          // .then((docRef) => {
          //   // Only emit event for actual appointments, not blocked time slots which lack a patientKey.
          //   if (newSlot.patientKey) {
          //     this.appointmentEventService.emitAppointmentSaved({ ...newSlot, id: docRef.id } as Appointment);
          //   }
          //   return docRef;
          // });
        });

        return from(Promise.all(savePromises)).pipe(
          map(() => void 0), // Map to void
          catchError((error: any) => {
            console.error('Error saving blocked appointments:', error);
            return of(void 0);
          })
        );
      })
    );
  }

  updateBlockedAppointment(id: string, updatedSlot: any): Observable<void> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.error('User not logged in. Cannot update blocked appointment.');
          return of(void 0);
        }

        const docRef = doc(this.firestore, PathConstants.getCalendarPath(user.uid), id);
        const dataToUpdate = { ...updatedSlot };

        if (dataToUpdate.start instanceof Date) {
          dataToUpdate.start = Timestamp.fromDate(dataToUpdate.start);
        }
        if (dataToUpdate.end instanceof Date) {
          dataToUpdate.end = Timestamp.fromDate(dataToUpdate.end);
        }

        return from(updateDoc(docRef, dataToUpdate)).pipe(
          map(() => {
            this.appointmentEventService.emitAppointmentUpdated({ ...updatedSlot, id: id } as Appointment);
            return void 0;
          }),
          catchError((error: any) => {
            console.error(`Error updating blocked appointment with ID ${id}:`, error);
            return of(void 0);
          })
        );
      })
    );
  }

  deleteBlockedAppointment(id: string): Observable<void> {
    return this.authService.getLoggedInUser().pipe(
      switchMap((user) => {
        if (!user) {
          console.error('User not logged in. Cannot delete blocked appointment.');
          return of(void 0);
        }
        const docRef = doc(this.firestore, PathConstants.getCalendarPath(user.uid), id);
        return from(deleteDoc(docRef)).pipe(
          map(() => {
            this.appointmentEventService.emitAppointmentDeleted({ id: id } as Appointment);
            return void 0;
          }),
          catchError((error: any) => {
            console.error(`Error deleting blocked appointment with ID ${id}:`, error);
            return of(void 0);
          })
        );
      })
    );
  }

  /**
   * Simulates an initial sync of all existing appointments to Google Calendar via a Cloud Function.
   * This would typically be called once after a successful connection.
   */
  initialSyncAllAppointmentsToGoogle(): void {
    console.log('[AppointmentService] Triggering initial sync of all existing appointments to Google Calendar.');
    this.authService
      .getLoggedInUser()
      .pipe(
        switchMap((user) => {
          if (!user) {
            console.warn('No user is logged in. Cannot perform initial sync.');
            return of([]);
          }
          return collectionData(collection(this.firestore, PathConstants.getAppointmentsPath(user.uid)), {
            idField: 'id',
          });
        }),
        take(1) // Take only the first emission
      )
      .subscribe((appointments) => {
        // This will now be handled by AutosyncCalendarService subscribing to events
      });
  }

  async updateAppointmentWithGoogleEventId(appointmentId: string, googleEventId: string): Promise<void> {
    const user = await this.authService.getLoggedInUser().pipe(take(1)).toPromise();
    if (!user?.uid) {
      console.error('User not logged in. Cannot update appointment with Google Event ID.');
      return;
    }
    const appointmentDocRef = doc(this.firestore, PathConstants.getAppointmentsPath(user.uid), appointmentId);
    await updateDoc(appointmentDocRef, { googleEventId: googleEventId });
    console.log(`Appointment ${appointmentId} updated with Google Event ID: ${googleEventId}`);
  }

  async saveAppointment(appointment: Appointment): Promise<string> {
    const user = await this.authService.getLoggedInUser().pipe(take(1)).toPromise();
    if (!user?.uid) {
      throw new Error('User not logged in. Cannot save appointment.');
    }
    const appointmentsCollection = collection(this.firestore, PathConstants.getAppointmentsPath(user.uid));
    const newAppointment = { ...appointment };
    if (newAppointment.start instanceof Date) {
      (newAppointment.start as any) = Timestamp.fromDate(newAppointment.start);
    }
    if (newAppointment.end instanceof Date) {
      (newAppointment.end as any) = Timestamp.fromDate(newAppointment.end);
    }
    const docRef = await addDoc(appointmentsCollection, newAppointment);
    this.appointmentEventService.emitAppointmentSaved({ ...newAppointment, id: docRef.id } as Appointment);
    return docRef.id;
  }

  async updateAppointment(appointment: Appointment): Promise<void> {
    // const user = await this.authService.getLoggedInUser().pipe(take(1)).toPromise();
    const user = this.authService.loggedInUser;
    if (!user?.uid) {
      throw new Error('User not logged in. Cannot update appointment.');
    }
    if (!appointment.id) {
      throw new Error('Appointment ID is required for update.');
    }
    const appointmentDocRef = doc(this.firestore, PathConstants.getAppointmentsPath(user.uid), appointment.id);
    const updatedAppointment = { ...appointment };
    if (updatedAppointment.start instanceof Date) {
      (updatedAppointment.start as any) = Timestamp.fromDate(updatedAppointment.start);
    }
    if (updatedAppointment.end instanceof Date) {
      (updatedAppointment.end as any) = Timestamp.fromDate(updatedAppointment.end);
    }
    await updateDoc(appointmentDocRef, updatedAppointment);
    this.appointmentEventService.emitAppointmentUpdated(updatedAppointment);
  }

  /**
   * Transforms raw appointment data by converting Firestore Timestamps to Dates,
   * filtering out cancelled/rescheduled appointments, and extracting HTTP links from notes.
   *
   * @param appointments Raw appointment data from Firestore
   * @returns Transformed and filtered appointments
   */
  private transformAndFilterAppointments(appointments: any[]): Appointment[] {
    return appointments
      .filter((appt) => this.isValidAppointmentStatus(appt.statusId))
      .map((appt) => this.transformAppointment(appt));
  }

  /**
   * Checks if an appointment status is valid (not cancelled or rescheduled).
   *
   * @param statusId The appointment status ID
   * @returns True if the status is valid
   */
  private isValidAppointmentStatus(statusId: AppointmentStatus): boolean {
    return statusId !== AppointmentStatus.CANCELLED && statusId !== AppointmentStatus.RESCHEDULED;
  }

  /**
   * Marks a patient appointment as COMPLETED (Seen) upon clinical evaluation via Cloud Function.
   */
  async markAppointmentComplete(
    providerOrgCode: string,
    patientKey: string,
    appointmentId: string
  ): Promise<UpdateAppointmentStatusResponse> {
    try {
      const result = await this.updateApptStatusFn({
        providerOrgCode,
        patientKey,
        appointmentId,
        action: 'COMPLETE',
      });
      return result.data;
    } catch (error: any) {
      console.error('[AppointmentService] Error completing appointment:', error);
      throw error;
    }
  }

  /**
   * Undoes a completed appointment, restoring it to its previous state (Scheduled / Checked In).
   */
  async undoAppointmentComplete(
    providerOrgCode: string,
    patientKey: string,
    appointmentId: string
  ): Promise<UpdateAppointmentStatusResponse> {
    try {
      const result = await this.updateApptStatusFn({
        providerOrgCode,
        patientKey,
        appointmentId,
        action: 'UNDO',
      });
      return result.data;
    } catch (error: any) {
      console.error('[AppointmentService] Error undoing appointment completion:', error);
      throw error;
    }
  }

  /**
   * Checks whether an appointment is eligible to be marked as Seen.
   * Allowed for appointments scheduled for Today or Yesterday (in doctor's local calendar date).
   * Future appointments or appointments older than yesterday cannot be marked as Seen.
   */
  isEligibleForCompletion(appointmentDate: Date | string | null): boolean {
    if (!appointmentDate) return false;
    const d = appointmentDate instanceof Date ? appointmentDate : new Date(appointmentDate);
    if (isNaN(d.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const apptDate = new Date(d);
    apptDate.setHours(0, 0, 0, 0);

    return apptDate.getTime() === today.getTime() || apptDate.getTime() === yesterday.getTime();
  }

  /**
   * Checks whether an appointment completion is eligible for Same-Day Undo.
   * Allowed ONLY if the completion (updateTime) occurred on the same calendar day (Today).
   * Historical completions (yesterday or earlier) are locked against doctor-side undo.
   */
  isEligibleForSameDayUndo(updateTime: Date | string | null): boolean {
    if (!updateTime) return false;
    const d = updateTime instanceof Date ? updateTime : new Date(updateTime);
    if (isNaN(d.getTime())) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const updatedDate = new Date(d);
    updatedDate.setHours(0, 0, 0, 0);

    return updatedDate.getTime() === today.getTime();
  }

  /**
   * Transforms a single appointment by converting timestamps and extracting links.
   *
   * @param appt Raw appointment data
   * @returns Transformed appointment
   */
  private transformAppointment(appt: any): Appointment {
    return {
      ...appt,
      start: appt.start instanceof Timestamp ? appt.start.toDate() : appt.start ? new Date(appt.start) : null,
      end: appt.end instanceof Timestamp ? appt.end.toDate() : appt.end ? new Date(appt.end) : null,
      updateTime:
        appt.updateTime instanceof Timestamp
          ? appt.updateTime.toDate()
          : appt.updateTime
            ? new Date(appt.updateTime)
            : null,
      link: this.extractHttpLink(appt.notes),
    } as Appointment;
  }

  /**
   * Extracts the first HTTP(S) link from a notes string.
   *
   * @param notes The notes text to search for links
   * @returns The first HTTP(S) URL found, or empty string if none found
   */
  private extractHttpLink(notes?: string): string {
    if (!notes) return '';
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matches = notes.match(urlRegex);
    return matches?.[0] ?? '';
  }
}
